import { useEffect, useState, useRef } from "react";
import Select from "react-select";
import moment from "moment";
import ReactPaginate from "react-paginate";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { useReactToPrint } from "react-to-print";
import { kayaCRUD } from "../../actions/kaya";
import { options } from "../../api";
import { Loading } from "../constant";
import { PrintHeader } from "../../layout/PrintHeader";
import { BoundaryCascade } from "./BoundaryCascade";

/**
 * Record-list report of KayaMember rows for a chosen boundary.
 * variant: 'live' (is_dead=False) | 'dead' (is_dead=True).
 * Print / Excel export the CURRENT PAGE only (raise page size for more).
 */
export const MemberListReport = ({ variant = "live" }) => {
  const isDead = variant === "dead";
  const title = isDead ? "Deceased Members" : "Members";

  const tableRef = useRef(null);
  const dataRef = useRef(null);

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [postsPerPage, setPostsPerPage] = useState(50);
  const [nextPage, setNextPage] = useState(0);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [boundaryId, setBoundaryId] = useState(null);
  const [boundaryName, setBoundaryName] = useState(null);

  const load = () => {
    setLoading(true);
    const fn = isDead ? kayaCRUD.loadKayaMemberDead : kayaCRUD.loadKayaMember;
    fn(postsPerPage, nextPage, value, boundaryId)
      .then((resp) => {
        setCount(resp.data.count);
        setData(resp.data.results);
        setPageCount(Math.ceil(resp.data.count / postsPerPage));
      })
      .finally(() => setLoading(false));
  };

  // Reload on page/size/search changes; boundary changes trigger via Generate button.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, postsPerPage, value, nextPage]);

  const handleChangePage = (e) => {
    setData([]);
    setPage(e.selected);
    setNextPage(e.selected * postsPerPage);
  };

  const handlePageSize = (sel) => {
    setData([]);
    setPostsPerPage(sel.value);
    setNextPage(0);
    setPage(0);
  };

  const handleGeneratePdf = useReactToPrint({ content: () => dataRef.current });

  const dval = (obj) => obj?.dictionary_item_name_en || "N/A";

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0">{title} — Record List</h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item"><a href="#">Reports</a></li>
                    <li className="breadcrumb-item active">{title}</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-header">
                  <BoundaryCascade onChange={(id, name) => { setBoundaryId(id); setBoundaryName(name); }} />
                  <div className="row align-items-end">
                    <div className="col-md-4">
                      <input className="form-control" value={value} placeholder="Search..."
                        onChange={(e) => { setData([]); setPage(0); setNextPage(0); setValue(e.target.value); }} />
                    </div>
                    <div className="col-md-3">
                      <button type="button" className="btn btn-primary" onClick={() => { setPage(0); setNextPage(0); load(); }}>
                        <i className="bi bi-funnel"></i> &nbsp; Generate{boundaryName ? ` — ${boundaryName}` : " (my area)"}
                      </button>
                    </div>
                    <div className="col-md-5">
                      <div className="d-flex justify-content-end gap-2">
                        <button type="button" className="btn btn-soft-danger" onClick={handleGeneratePdf}>
                          <i className="bi bi-file-earmark-pdf"></i> PDF
                        </button>
                        <button type="button" className="btn btn-soft-success">
                          <DownloadTableExcel filename={`${title}`} sheet="members" currentTableRef={tableRef.current}>
                            <i className="bi bi-file-earmark-excel"></i> Excel
                          </DownloadTableExcel>
                        </button>
                      </div>
                    </div>
                  </div>
                  <small className="text-muted">Print/Excel export the current page only ({postsPerPage} rows). Increase page size for more.</small>
                </div>

                <div className="card-body" ref={dataRef}>
                  <PrintHeader subtitle={`${title} List${boundaryName ? ` — ${boundaryName}` : ""}`} />
                  <table ref={tableRef} className="table table-bordered table-striped align-middle" style={{ width: "100%" }}>
                    <thead>
                      <tr>
                        <th>SR No.</th>
                        <th>Full Name</th>
                        <th>Reg No.</th>
                        <th>Gender</th>
                        <th>Date of Birth</th>
                        <th>Phone</th>
                        <th>Relation to Head</th>
                        {isDead && <th>Death Date</th>}
                        {isDead && <th>Cause</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {data?.map((dt, i) => {
                        const m = dt.member_id || {};
                        return (
                          <tr key={i}>
                            <td>{nextPage + i + 1}</td>
                            <td>{m.full_name || "N/A"}</td>
                            <td>{m.reg_no || "N/A"}</td>
                            <td>{dval(m.dictionary_gender)}</td>
                            <td>{m.dob ? moment(m.dob).format("D MMM, YYYY") : "N/A"}</td>
                            <td>{m.phone_number || "N/A"}</td>
                            <td>{dval(m.dictionary_relation_to_head)}</td>
                            {isDead && <td>{m.death_date ? moment(m.death_date).format("D MMM, YYYY") : "N/A"}</td>}
                            {isDead && <td>{dval(m.dictionary_death_causes)}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="row">{!data.length && <Loading />}</div>
                </div>

                <div className="card-footer">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <p className="mb-0">Showing {data.length} of {count} entries</p>
                    </div>
                    <div className="col-md-4">
                      <Select options={options} value={{ value: postsPerPage, label: String(postsPerPage) }} onChange={handlePageSize} />
                    </div>
                    <div className="col-md-5 paginate">
                      <ReactPaginate
                        previousLabel={<i className="las la-angle-double-left la-lg"></i>}
                        nextLabel={<i className="las la-angle-double-right la-lg"></i>}
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={3}
                        onPageChange={handleChangePage}
                        containerClassName={"pagination"}
                        activeClassName={"active"}
                        forcePage={page}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberListReport;
